/**
 * 统一的认证弹窗组件
 * 支持 Email OTP、Google OAuth、Apple Sign In
 * 参考 lovart.ai 的设计风格
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { getClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import TurnstileWidget from '@/components/TurnstileWidget';
import { useTranslations } from 'next-intl';
import { sendGAEvent } from '@/utils/analytics';
import { prepareGuestMediaHandoff } from '@/lib/auth/guest-media-handoff-client';

const OAUTH_NEXT_PATH_COOKIE = 'botgo_oauth_next_path';
const AUTH_INIT_RETRY_DELAYS = [0, 200, 500];
const CAPTCHA_VERIFICATION_ERROR = 'Security verification failed. Please try again.';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function AuthModal({ isOpen, onClose, redirectPath }) {
  const t = useTranslations('Auth');
  const [step, setStep] = useState('initial'); // initial, otp, success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const envTurnstileDisabled = process.env.NEXT_PUBLIC_DISABLE_TURNSTILE === 'true';
  const isTurnstileDisabled = envTurnstileDisabled;

  const supabase = getClient();
  const router = useRouter();
  const turnstileRef = useRef(null);
  const otpSendingRef = useRef(false);

  const syncServerSession = async (session) => {
    if (!session || typeof session === 'string') return;

    const response = await fetch('/auth/callback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        event: 'SIGNED_IN',
        session,
      }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error || 'Failed to sync auth session');
    }
  };

  const initializeUser = async () => {
    let lastError = null;

    for (const delayMs of AUTH_INIT_RETRY_DELAYS) {
      if (delayMs > 0) {
        await sleep(delayMs);
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        try {
          await syncServerSession(sessionData.session);
        } catch (syncError) {
          console.warn('Failed to sync auth session before init-user:', syncError);
        }
      }

      const response = await fetch('/api/auth/init-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        return result;
      }

      lastError = new Error(result.error || 'Failed to initialize user');

      // OTP 刚验证成功时，服务端有时会比浏览器晚一步拿到新 session。
      if (response.status !== 401) {
        break;
      }
    }

    throw lastError || new Error('Failed to initialize user');
  };

  // 重置倒计时
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // 关闭弹窗时重置状态
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep('initial');
        setEmail('');
        setOtp(['', '', '', '', '', '']);
        setError('');
        setSuccessMessage('');
        setTurnstileToken('');
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isTurnstileDisabled) {
      setTurnstileToken('local-bypass');
    }
  }, [isTurnstileDisabled]);

  const resolveNextPath = () => {
    if (typeof window === 'undefined') return '/';

    const fallbackPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (!redirectPath) {
      return fallbackPath || '/';
    }

    try {
      const redirectUrl = new URL(redirectPath, window.location.origin);
      if (redirectUrl.origin !== window.location.origin) {
        return fallbackPath || '/';
      }
      return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}` || '/';
    } catch (error) {
      console.warn('Invalid redirectPath, fallback to current page:', error);
      return fallbackPath || '/';
    }
  };

  // Google 登录
  const handleGoogleSignIn = async () => {
    // 触发 GA 事件
    sendGAEvent('click_google_login');

    // 验证 Turnstile
    if (!turnstileToken && !isTurnstileDisabled) {
      setError(t('errors.securityVerification'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const nextPath = resolveNextPath();
      const cookieParts = [
        `${OAUTH_NEXT_PATH_COOKIE}=${encodeURIComponent(nextPath)}`,
        'Path=/',
        'Max-Age=600',
        'SameSite=Lax',
      ];
      if (window.location.protocol === 'https:') {
        cookieParts.push('Secure');
      }
      document.cookie = cookieParts.join('; ');

      await prepareGuestMediaHandoff(supabase);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message || t('errors.googleSignIn'));
    } finally {
      setLoading(false);
    }
  };

  // Apple 登录
  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const nextPath = resolveNextPath();
      const cookieParts = [
        `${OAUTH_NEXT_PATH_COOKIE}=${encodeURIComponent(nextPath)}`,
        'Path=/',
        'Max-Age=600',
        'SameSite=Lax',
      ];
      if (window.location.protocol === 'https:') {
        cookieParts.push('Secure');
      }
      document.cookie = cookieParts.join('; ');

      await prepareGuestMediaHandoff(supabase);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (err) {
      setError(err.message || 'Failed to sign in with Apple');
    } finally {
      setLoading(false);
    }
  };

  // 发送 OTP
  const handleSendOTP = async () => {
    if (otpSendingRef.current) return;
    // 验证 Turnstile
    if (!turnstileToken && !isTurnstileDisabled) {
      setError(t('errors.securityVerification'));
      return;
    }

    if (!email) return;

    otpSendingRef.current = true;
    const captchaToken = turnstileToken;
    setTurnstileToken('');
    setLoading(true);
    setError('');

    try {
      const precheckResponse = await fetch('/api/auth/email-precheck', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!precheckResponse.ok) {
        const precheckResult = await precheckResponse.json().catch(() => ({}));
        throw new Error(precheckResult.error || t('errors.sendCode'));
      }

      await prepareGuestMediaHandoff(supabase);
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          captchaToken: isTurnstileDisabled ? undefined : captchaToken,
        },
      });

      if (error) throw error;

      setStep('otp');
      setResendCountdown(41); // 41 秒倒计时
      setSuccessMessage(t('success'));

      // 清除成功消息
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError(err.message || t('errors.sendCode'));
    } finally {
      otpSendingRef.current = false;
      turnstileRef.current?.reset();
      setLoading(false);
    }
  };

  // 验证 OTP
  const handleVerifyOTP = async (otpValue) => {
    setLoading(true);
    setError('');

    try {
      await prepareGuestMediaHandoff(supabase);
      // 先尝试 email 类型（已有用户登录）
      let verifyResult = await supabase.auth.verifyOtp({
        email,
        token: otpValue,
        type: 'email',
      });

      // 如果失败，尝试 signup 类型（新用户注册）
      if (verifyResult.error && verifyResult.error.message.includes('invalid')) {
        verifyResult = await supabase.auth.verifyOtp({
          email,
          token: otpValue,
          type: 'signup',
        });
      }

      if (verifyResult.error) throw verifyResult.error;

      const verifiedSession =
        verifyResult.data?.session ||
        (await supabase.auth.getSession()).data?.session ||
        null;

      if (verifiedSession) {
        await syncServerSession(verifiedSession);
      }

      // ===== OTP 验证成功，初始化用户记录 =====
      try {
        const result = await initializeUser();

        console.log('User initialization result:', result);

        // 验证成功，关闭弹窗
        setStep('success');
        sendGAEvent('login_success', { method: 'email_otp' });

        setTimeout(() => {
          onClose();

          // 根据是否需要邀请码决定跳转路径
          if (result.requireInvite) {
            // 需要邀请码，添加 URL 参数
            window.location.href = '/?require_invite=true';
          } else if (redirectPath) {
            // 有重定向路径
            router.push(redirectPath);
          } else {
            // 刷新当前页面
            window.location.reload();
          }
        }, 1000);
      } catch (initError) {
        console.error('Failed to initialize user:', initError);
        setError(t('errors.initUser'));
        return;
      }
    } catch (err) {
      setError(err.message || t('errors.invalidCode'));
      setOtp(['', '', '', '', '', '']); // 清空 OTP
    } finally {
      setLoading(false);
    }
  };

  // 处理 OTP 输入
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // 只允许数字

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // 自动聚焦到下一个输入框
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // 如果填满了所有 6 位，自动验证
    if (newOtp.every(digit => digit !== '') && newOtp.length === 6) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // 处理 OTP 删除
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // 重新发送 OTP
  const handleResendOTP = () => {
    if (resendCountdown > 0) return;
    handleSendOTP();
  };

  // 返回初始页面
  const handleBack = () => {
    setStep('initial');
    setOtp(['', '', '', '', '', '']);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div data-botgo-modal="auth" className="fixed inset-0 z-50 flex min-h-screen items-center justify-center overflow-y-auto p-4 sm:p-6">
      {/* 背景遮罩 - 半透明模糊效果 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 弹窗内容 - 深色渐变背景 */}
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 shadow-2xl border border-neutral-700">
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-neutral-800"
        >
          <X className="h-5 w-5" />
        </button>

        {/* 返回按钮（仅在 OTP 页面显示） */}
        {step === 'otp' && (
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 z-10 text-gray-400 transition-colors hover:text-gray-300"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div className="p-8">
          {/* Logo 和标题 */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Image
                src="/brand/botgo-app-icon-512.png?v=orbit-centered-03"
                alt="BotGo AI"
                width={48}
                height={48}
                className="rounded-xl"
              />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {step === 'otp' ? t('titleCheckEmail') : t('titleWelcome')}
            </h1>
            {step === 'initial' && (
              <p className="text-gray-300 text-sm">
                {t('subtitle')}
              </p>
            )}
            {step === 'otp' && (
              <p className="text-gray-300 text-sm mt-3">
                {t('sentCode')}<br />
                <span className="font-medium text-white">{email}</span>
              </p>
            )}
          </div>

          {/* 初始页面 */}
          {step === 'initial' && (
            <>
              {/* OAuth 按钮 */}
              <div className="space-y-3 mb-6">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-neutral-700 hover:bg-neutral-600 border border-neutral-600 text-white font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('continueGoogle')}
                </button>

                {/* Apple Sign In - 暂时隐藏 */}
                {/* <button
                  onClick={(e) => e.preventDefault()}
                  disabled={true}
                  className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-400 font-medium py-3 px-4 rounded-lg cursor-not-allowed opacity-50"
                  title="Apple Sign In will be available soon"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  with Apple
                </button> */}
              </div>

              {/* 分隔线 */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-gray-400">{t('or')}</span>
                </div>
              </div>

              {/* Email 输入框 */}
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-neutral-800 border border-neutral-600 rounded-lg text-white text-center placeholder:text-gray-400 focus:outline-none focus:border-amber-500 transition-all caret-white"
                />

                {/* Turnstile 验证组件 */}
                <div className="flex justify-center">
                  {!isTurnstileDisabled && (
                    <TurnstileWidget
                      ref={turnstileRef}
                      action="auth_email"
                      onExpire={() => setTurnstileToken('')}
                      onVerify={(token) => {
                        console.log('Turnstile verification success');
                        setTurnstileToken(token);
                        setError((current) => current === CAPTCHA_VERIFICATION_ERROR ? '' : current);
                      }}
                      onError={() => {
                        console.error('Turnstile verification failed');
                        setTurnstileToken('');
                        setError(CAPTCHA_VERIFICATION_ERROR);
                      }}
                      theme="dark"
                    />
                  )}
                </div>

                {/* 成功消息 */}
                {successMessage && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">{successMessage}</span>
                  </div>
                )}

                {/* 错误消息 */}
                {error && (
                  <div className="text-red-400 text-sm bg-red-900/30 px-4 py-2 rounded-lg text-center border border-red-800/50">
                    {error}
                  </div>
                )}

                {/* Continue with Email 按钮 */}
                <button
                  onClick={handleSendOTP}
                  disabled={!email || loading}
                  className={`w-full flex items-center justify-center gap-3 font-medium py-3 px-4 rounded-lg transition-all ${!email || loading
                    ? 'bg-neutral-700 border border-neutral-600 text-gray-500 cursor-not-allowed opacity-50'
                    : 'bg-neutral-700 hover:bg-neutral-600 border border-neutral-600 text-white'
                    }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    t('continueEmail')
                  )}
                </button>
              </div>

              {/* 服务条款 */}
              <p className="text-center text-gray-400 text-xs mt-6">
                {t('termsAgreement')}{' '}
                <a href="/terms-of-service" className="text-gray-300 hover:text-white hover:underline" target="_blank" rel="noopener noreferrer">
                  {t('termsOfUse')}
                </a>{' '}
                {t('and')}{' '}
                <a href="/privacy-policy" className="text-gray-300 hover:text-white hover:underline" target="_blank" rel="noopener noreferrer">
                  {t('privacyPolicy')}
                </a>
              </p>
            </>
          )}

          {/* OTP 验证页面 */}
          {step === 'otp' && (
            <div className="space-y-6">
              {/* OTP 输入框 */}
              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-semibold text-white bg-neutral-800 border-2 border-neutral-600 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                    disabled={loading}
                  />
                ))}
              </div>

              {/* 错误消息 */}
              {error && (
                <div className="text-red-400 text-sm text-center bg-red-900/30 px-4 py-2 rounded-lg border border-red-800/50">
                  {error}
                </div>
              )}

              {!isTurnstileDisabled && (
                <TurnstileWidget ref={turnstileRef} action="auth_email" theme="dark"
                  onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')} />
              )}

              {/* 重新发送 */}
              <div className="text-center">
                {resendCountdown > 0 ? (
                  <p className="text-gray-400 text-sm">
                    {t('resendIn', { seconds: resendCountdown })}
                  </p>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={loading || (!isTurnstileDisabled && !turnstileToken)}
                    className="text-gray-300 text-sm font-medium hover:text-white hover:underline"
                  >
                    {t('resendCode')}
                  </button>
                )}
              </div>

              {/* 加载状态 */}
              {loading && (
                <div className="flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                </div>
              )}
            </div>
          )}

          {/* 成功页面 */}
          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('welcomeBack')}
              </h2>
              <p className="text-gray-300">
                {t('redirecting')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
